import cn from 'classnames';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

import postRenewPasswordSendLink from '@/common/api/users/public/postRenewPasswordSendLink';
import postRenewPasswordSendLinkAppCrafter from '@/common/api/users/public/postRenewPasswordSendLinkAppCrafter';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import Snack from '@/common/components/molecules/Snack/Snack';
import TextField from '@/common/components/molecules/TextField/TextField';
import RecaptchaContextProvider, {
  useRecaptcha,
} from '@/common/contexts/RecaptchaContext';
import getRecaptchaToken from '@/common/helpers/getRecaptchaToken';
import useTheme from '@/common/hooks/useTheme';
import useToken from '@/common/hooks/useToken';

import {
  ForgotPasswordFormContainerDefaultProps,
  ForgotPasswordFormContainerPropTypes,
  ForgotPasswordFormDefaultProps,
  ForgotPasswordFormPropTypes,
} from './ForgotPasswordForm.proptypes';

const ForgotPasswordForm = ({
  isLoading,
  onSuccess,
  className,
  submitLabel,
  isAppCrafter,
}) => {
  const { t } = useTranslation();
  const { defaultI18n } = useTheme();
  const token = useToken();

  const [identifierParam, setIdentifierParam] = useState('');

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    setIdentifierParam(params.get('identifier'));
  }, []);

  const {
    mutateAsync,
    isLoading: isLoadingRenewPasswordSendLink,
  } = useMutation(
    isAppCrafter
      ? postRenewPasswordSendLinkAppCrafter
      : postRenewPasswordSendLink,
  );
  const { isLoading: isLoadingRecaptcha } = useRecaptcha();

  const isDisabled = useMemo(() => {
    const loading =
      isLoading || isLoadingRenewPasswordSendLink || isLoadingRecaptcha;

    return {
      loading,
      disabled: loading,
    };
  }, [isLoading, isLoadingRenewPasswordSendLink, isLoadingRecaptcha]);

  const submitForm = (identifier, recaptchaToken) =>
    mutateAsync({ identifier, recaptcha_token: recaptchaToken, token })
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        toast.success(
          <Snack message={t('common:messages:forms.success.renew_password')} />,
        );
      })
      .catch((error) => {
        if (error.response?.status === 500) {
          toast.error(
            <Snack message={t('common:messages:forms.errors.server_error')} />,
          );
        } else {
          toast.error(
            <Snack message={t('common:messages:forms.errors.default')} />,
          );
        }
      });

  const handleSubmit = ({ identifier }) => {
    if (process.env.REACT_APP_RECAPTCHA_ACTIVE === 'true') {
      return getRecaptchaToken()
        .then((recaptchaToken) => submitForm(identifier, recaptchaToken))
        .catch((error) => {
          toast.error(
            <Snack
              message={t('common:messages:forms.errors.recaptcha_error')}
            />,
          );
          throw error;
        });
    }
    // Utiliser le token temporaire si reCAPTCHA est désactivé
    return submitForm(identifier, process.env.REACT_APP_RECAPTCHA_TOKEN);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        identifier: identifierParam || '',
      }}
      disabled={isLoading || isLoadingRecaptcha}
      onSubmit={handleSubmit}
    >
      {() => (
        <Form
          className={cn('form form--forgot-password', className)}
          data-qa-id="forgot-password-form"
        >
          <Field
            as={TextField}
            context="identifier-field"
            disabled={isDisabled.disabled}
            id="identifier"
            name="identifier"
            required
          >
            {t(`${defaultI18n}:messages:forms.fields.identifier`)}
          </Field>
          <ButtonLoading
            context="forgot-password-save"
            disabled={isDisabled.disabled}
            isLoading={isDisabled.loading}
            className="login-form__submit"
          >
            {submitLabel || t('common:messages:actions.send')}
          </ButtonLoading>
        </Form>
      )}
    </Formik>
  );
};

ForgotPasswordForm.defaultProps = {
  ...ForgotPasswordFormDefaultProps,
};

ForgotPasswordForm.propTypes = {
  ...ForgotPasswordFormPropTypes,
};

const ForgotPasswordFormContainer = ({
  isLoading,
  onSuccess,
  className,
  submitLabel,
  isAppCrafter,
}) => (
  <RecaptchaContextProvider>
    <ForgotPasswordForm
      isLoading={isLoading}
      onSuccess={onSuccess}
      className={className}
      submitLabel={submitLabel}
      isAppCrafter={isAppCrafter}
    />
  </RecaptchaContextProvider>
);

ForgotPasswordFormContainer.defaultProps = {
  ...ForgotPasswordFormContainerDefaultProps,
};

ForgotPasswordFormContainer.propTypes = {
  ...ForgotPasswordFormContainerPropTypes,
};

export default ForgotPasswordFormContainer;
